import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, Lock, TrendingUp } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import DoctorShell from "@/components/DoctorShell";

export default function DoctorFeatures() {
    const [entitlement, setEntitlement] = useState(null);
    const [doctor, setDoctor] = useState(null);
    const [filter, setFilter] = useState("all");

    useEffect(() => {
        Promise.all([
            api.get("/me/entitlements"),
            api.get("/me/doctor"),
        ])
            .then(([e, d]) => {
                setEntitlement(e.data);
                setDoctor(d.data);
            })
            .catch((err) =>
                toast.error(errMsg(err, "Failed to load"))
            );
    }, []);

    const byModule = useMemo(() => {
        if (!entitlement) return [];
        const map = {};
        for (const it of entitlement.items) {
            const key = it.module_code;
            if (!map[key]) map[key] = { code: it.module_code, name: it.module_name, items: [] };
            if (filter === "unlocked" && !it.unlocked) continue;
            if (filter === "locked" && it.unlocked) continue;
            map[key].items.push(it);
        }
        return Object.values(map).sort((a, b) => a.code.localeCompare(b.code));
    }, [entitlement, filter]);

    return (
        <DoctorShell doctor={doctor}>
            <div className="animate-fade-up">
                <div className="text-xs uppercase tracking-widest text-[#0F766E] font-semibold">
                    Entitlement
                </div>
                <h1 className="mt-1 text-3xl font-bold text-[#123B5D]">
                    Your features
                </h1>
                <p className="mt-1 text-sm text-[#17212B]/60">
                    Package:{" "}
                    <span className="font-semibold text-[#123B5D]">
                        {entitlement?.package_name || "None"}
                    </span>{" "}
                    · <span data-testid="entitlement-unlocked-count">{entitlement?.unlocked_count || 0}</span> unlocked
                    · <span data-testid="entitlement-locked-count">{entitlement?.locked_count || 0}</span> locked
                    · <span data-testid="entitlement-total">{entitlement?.total_features || 0}</span> total
                </p>

                <div className="mt-6 flex items-center gap-2 text-sm">
                    {[
                        ["all", "All"],
                        ["unlocked", "Unlocked"],
                        ["locked", "Locked"],
                    ].map(([k, l]) => (
                        <button
                            key={k}
                            className={
                                "px-3 py-1.5 rounded-full font-semibold transition-colors " +
                                (filter === k
                                    ? "bg-[#123B5D] text-white"
                                    : "bg-[#123B5D]/5 text-[#123B5D] hover:bg-[#123B5D]/10")
                            }
                            onClick={() => setFilter(k)}
                            data-testid={`features-filter-${k}`}
                        >
                            {l}
                        </button>
                    ))}
                </div>

                <div className="mt-8 space-y-6">
                    {byModule.map((mod) => (
                        <section key={mod.code} data-testid={`features-module-${mod.code}`}>
                            <div className="flex items-baseline justify-between mb-3">
                                <div>
                                    <div className="text-[11px] uppercase tracking-widest text-[#17212B]/50">
                                        Module {mod.code}
                                    </div>
                                    <div className="font-semibold text-[#123B5D]">
                                        {mod.name}
                                    </div>
                                </div>
                                <div className="text-xs text-[#17212B]/60">
                                    {mod.items.length} features
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                {mod.items.map((it) => (
                                    <div
                                        key={it.feature_code}
                                        className={
                                            "ddi-card p-4 flex flex-col justify-between " +
                                            (it.unlocked ? "" : "ddi-locked")
                                        }
                                        data-testid={`feature-card-${it.feature_code}`}
                                    >
                                        <div>
                                            <div className="flex items-center gap-2">
                                                {it.unlocked ? (
                                                    <span className="h-5 w-5 rounded-full bg-[#0F766E]/10 text-[#0F766E] flex items-center justify-center">
                                                        <Check size={12} />
                                                    </span>
                                                ) : (
                                                    <span className="h-5 w-5 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center">
                                                        <Lock size={11} />
                                                    </span>
                                                )}
                                                <div className="font-medium text-[#17212B]">
                                                    {it.name}
                                                </div>
                                            </div>
                                            <div className="mt-2 text-[11px] text-[#17212B]/50 font-mono">
                                                {it.feature_code}
                                            </div>
                                            {it.status === "coming_soon" ? (
                                                <div className="mt-2 text-[10px] uppercase tracking-widest text-[#D97706]">
                                                    Coming soon
                                                </div>
                                            ) : null}
                                        </div>
                                        {!it.unlocked && it.status === "active" && it.upgrade_to ? (
                                            <div className="mt-3">
                                                <Link
                                                    to="/doctor/packages"
                                                    className="inline-flex items-center gap-1 text-xs font-semibold text-[#0F766E] hover:underline"
                                                    data-testid={`upgrade-cta-${it.feature_code}`}
                                                >
                                                    <TrendingUp size={12} />
                                                    Upgrade to {it.upgrade_to} to unlock
                                                </Link>
                                            </div>
                                        ) : null}
                                    </div>
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            </div>
        </DoctorShell>
    );
}
